import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";
import { UseFormSetError, UseFormWatch } from "react-hook-form";

import { useUser } from "@shared/auth";

import * as constants from "src/constants";
import updateNewsletterSubscription from "src/lib/graphql/update-newsletter-subscription";
import { rememberLastLoginMethod } from "src/lib/last-login-method";

const RECAPTCHA_SCRIPT_TIMEOUT = 10_000;
const RECAPTCHA_SCRIPT_POLL_INTERVAL = 100;
const RECAPTCHA_UNAVAILABLE_MESSAGE = "reCAPTCHA script is not available";

/**
 * Thrown when the reCAPTCHA script doesn't load, e.g. because a content blocker blocks it.
 */
export class RecaptchaUnavailableError extends Error {
  constructor() {
    super(RECAPTCHA_UNAVAILABLE_MESSAGE);
    this.name = "RecaptchaUnavailableError";
  }
}

/**
 * Resolves once the reCAPTCHA script has loaded. It loads after the page became
 * interactive, so users can submit the form before it's available. Rejecting right
 * away would show them the reCAPTCHA v2 challenge for no reason.
 */
export async function waitForRecaptcha(
  timeout = RECAPTCHA_SCRIPT_TIMEOUT,
): Promise<ReCaptchaEnterpriseInstance> {
  const start = Date.now();

  while (!window.grecaptcha?.enterprise) {
    if (Date.now() - start >= timeout) {
      throw new RecaptchaUnavailableError();
    }
    await new Promise((resolve) =>
      setTimeout(resolve, RECAPTCHA_SCRIPT_POLL_INTERVAL),
    );
  }

  return window.grecaptcha.enterprise;
}

async function getRecaptchaToken(): Promise<string> {
  const recaptcha = await waitForRecaptcha();

  return new Promise((resolve, reject) => {
    recaptcha.ready(async () => {
      try {
        const recaptchaToken = await recaptcha.execute(
          process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
          { action: "signup" },
        );
        resolve(recaptchaToken);
      } catch (error) {
        reject(error);
      }
    });
  });
}

async function getRecaptchaV2Token(widgetId: number): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!window.grecaptcha?.enterprise) {
      reject(
        new Error(
          "reCAPTCHA is not available. Please ensure the reCAPTCHA script is loaded.",
        ),
      );
      return;
    }

    window.grecaptcha.enterprise.ready(() => {
      try {
        const token = window.grecaptcha.enterprise.getResponse(widgetId);
        if (token) {
          resolve(token);
        } else {
          reject(new Error("reCAPTCHA v2 challenge not completed"));
        }
      } catch (error) {
        reject(error);
      }
    });
  });
}

type FormInput = {
  firstName?: string;
  lastName?: string;
  email?: string;
  code?: string;
  password?: string;
  newsletter?: boolean;
};

type UseFormSubmissionHandlerArgs = {
  preferredLoginMethod: string;
  watch: UseFormWatch<FormInput>;
  user: ReturnType<typeof useUser>;
  setError: UseFormSetError<FormInput>;
  dryRun?: boolean;
};

/**
 * Defines react callbacks for the authentication form.
 * Encapsuling the logic in this hook makes the component logic
 * easier to grasp.
 */
export function useFormSubmissionHandler({
  preferredLoginMethod,
  watch,
  user,
  setError,
  dryRun = false,
}: UseFormSubmissionHandlerArgs) {
  const t = useTranslations(
    `${constants.MESSAGE_PREFIX}components.AuthenticationForm`,
  );
  const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);

  /**
   * Updates the form state based on the response from the server.
   * @param response the response from the server
   */
  const updateFormErrorState = useCallback(
    async (response: Response) => {
      if (!response.ok) {
        const body = await response.json();

        if (response.status === 429) {
          setError("email", {
            type: "custom",
            message: t("errors.tooManyRequests"),
          });
        }

        if (response.status === 400) {
          if (body?.message === RECAPTCHA_UNAVAILABLE_MESSAGE) {
            // The v2 challenge needs the same script, so we let the user retry instead
            setError("email", {
              type: "custom",
              message: t("errors.recaptchaUnavailable"),
            });
          } else if ((body?.message ?? "").match(/recaptcha/gi)) {
            // If we haven't shown v2 yet, trigger the fallback
            if (!showRecaptchaV2) {
              setShowRecaptchaV2(true);
              // Don't set error message yet - wait for v2 completion
            } else {
              setError("email", {
                type: "custom",
                message: t("errors.recaptcha"),
              });
            }
          }

          if (body?.message === "Password is wrong") {
            setError("password", {
              type: "custom",
              message: t("errors.passwordWrong"),
            });
          }

          if (body?.message === "Code is wrong") {
            setError("code", {
              type: "codeWrong",
              message: t("errors.codeWrong"),
            });
          }

          if (body?.message === "Code is expired") {
            setError("code", {
              type: "codeExpired",
              message: t("errors.codeExpired"),
            });
          }
        } else {
          setError("email", {
            type: "custom",
            message: `Error: ${response.status} – ${body?.message}`,
          });
        }
      } else {
        // Success - clear v2 fallback state
        setShowRecaptchaV2(false);
      }
    },
    [setError, t, showRecaptchaV2],
  );

  /**
   * Uses the preferedLoginMethod to determine
   * which endpoint to use for authenticating the user.
   * @returns a promise with the response from the server
   */
  const submitFormRequest = useCallback(
    async (values: FormInput) => {
      switch (preferredLoginMethod) {
        case "password": {
          const response = await user.login({
            email: values.email,
            password: values.password,
          });
          if (response.ok) {
            rememberLastLoginMethod("email");
          }
          if (response.ok && values.newsletter) {
            await updateNewsletterSubscription({
              active: true,
              source: "signup",
            });
          }
          await updateFormErrorState(response);
          return response;
        }
        case "passwordless": {
          const response = await user.login({
            email: values.email,
            code: values.code,
          });
          if (response.ok) {
            rememberLastLoginMethod("email");
          }
          if (response.ok && values.newsletter) {
            await updateNewsletterSubscription({
              active: true,
              source: "signup",
            });
          }
          await updateFormErrorState(response);
          return response;
        }
        default: {
          const response = await getAuthCode(user, values, dryRun);
          await updateFormErrorState(response);
          return response;
        }
      }
    },
    [preferredLoginMethod, user, dryRun, updateFormErrorState],
  );

  /**
   * Requests a new authentication code from the server.
   * This can be useful when the user has not received the code
   * or if the code has expired.
   * @returns a promise with the response from the server
   */
  const reRequestAuthCode = useCallback(async () => {
    const formValues: FormInput = {
      email: watch("email"),
      firstName: watch("firstName"),
      lastName: watch("lastName"),
      newsletter: watch("newsletter"),
    };
    const response = await getAuthCode(user, formValues, dryRun);

    await updateFormErrorState(response);
  }, [user, watch, dryRun, updateFormErrorState]);

  /**
   * Retries the signup request with a reCAPTCHA v2 token after the challenge is completed.
   */
  const retryWithRecaptchaV2 = useCallback(
    async (widgetId?: number) => {
      if (!showRecaptchaV2 || widgetId === undefined) {
        return;
      }

      // Get current form values.
      // This ensures any edits made while the v2 widget is displayed are included
      const currentFormValues: FormInput = {
        email: watch("email"),
        firstName: watch("firstName"),
        lastName: watch("lastName"),
        newsletter: watch("newsletter"),
      };

      try {
        const response = await getAuthCode(
          user,
          currentFormValues,
          dryRun,
          widgetId,
        );

        await updateFormErrorState(response);
      } catch (error) {
        setError("email", {
          type: "custom",
          message: t("errors.recaptcha"),
        });
      }
    },
    [showRecaptchaV2, watch, user, dryRun, updateFormErrorState, setError, t],
  );

  return {
    submitFormRequest,
    updateFormErrorState,
    reRequestAuthCode,
    showRecaptchaV2,
    retryWithRecaptchaV2,
  };
}

useFormSubmissionHandler.messages = [
  `${constants.MESSAGE_PREFIX}components.AuthenticationForm`,
];

async function getAuthCode(
  user: ReturnType<typeof useUser>,
  values: FormInput,
  dry = false,
  recaptchaV2WidgetId?: number,
) {
  let recaptchaToken: string | undefined;
  if (!dry) {
    try {
      if (recaptchaV2WidgetId === undefined || recaptchaV2WidgetId === null) {
        recaptchaToken = await getRecaptchaToken();
      } else {
        recaptchaToken = await getRecaptchaV2Token(recaptchaV2WidgetId);
      }
    } catch (error) {
      // Return a response that will trigger error handling
      return new Response(
        JSON.stringify({
          statusCode: 400,
          error: "Bad Request",
          message:
            error instanceof RecaptchaUnavailableError
              ? RECAPTCHA_UNAVAILABLE_MESSAGE
              : "reCAPTCHA verification failed",
        }),
        {
          status: 400,
          statusText: "Bad Request",
          headers: { "Content-Type": "application/json" },
        },
      );
    }
  }
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return user.requestAuthCode({
    firstName: values.firstName || undefined,
    lastName: values.lastName || undefined,
    email: values.email,
    newsletter: values.newsletter,
    timezone,
    recaptchaToken,
    recaptchaVersion:
      recaptchaV2WidgetId === undefined || recaptchaV2WidgetId === null
        ? "v3"
        : "v2",
    dry,
  });
}
